import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { validateTCKimlik } from '../utils/tcValidation';

const CONTACT_TYPES = [
  { value: '', label: 'Tümü' },
  { value: 'buyer', label: '🛒 Alıcılar' },
  { value: 'seller', label: '🏪 Satıcılar' },
  { value: 'both', label: '↔️ İkisi de' },
];

const CONTACT_CATEGORIES = [
  { value: 'toptanci', label: 'Toptancı' },
  { value: 'manav', label: 'Manav' },
  { value: 'market', label: 'Market' },
  { value: 'ihracatci', label: 'İhracatçı' },
  { value: 'tarim_kredi', label: 'Tarım Kredi' },
  { value: 'akaryakit', label: 'Akaryakıt' },
  { value: 'tarim_ilac', label: 'Tarım İlaç' },
  { value: 'tohum_bayii', label: 'Tohum Bayii' },
  { value: 'gubre_bayii', label: 'Gübre Bayii' },
  { value: 'diger', label: 'Diğer' },
];

const WORK_TYPES_LIST = ['Hasat','Budama','Sulama','Ekim/Dikim','İlaçlama','Çapalama','Gübreleme','Paketleme','Taşıma','Diğer'];

const EMPTY_CONTACT = { type: 'both', contactCategory: 'diger', companyName: '', isIndividual: false, name: '', phone: '', email: '', city: '', district: '', taxNumber: '', iban: '', notes: '' };
const EMPTY_WORKER = { firstName: '', lastName: '', identityType: 'tc', identityNumber: '', nationality: 'Türkiye', phone: '', city: '', district: '', workTypes: [], defaultDailyRate: '', preferredUnit: 'gün', isRegional: false, notes: '' };

export default function ContactsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('contacts');

  return (
    <div>
      <h2 className="section-title">{t('nav.contacts')}</h2>
      <div className="settings-tabs" style={{ marginBottom: 12 }}>
        <button className={`settings-tab${activeTab === 'contacts' ? ' active' : ''}`} onClick={() => setActiveTab('contacts')}>
          🏪 Alıcı / Satıcılar
        </button>
        <button className={`settings-tab${activeTab === 'workers' ? ' active' : ''}`} onClick={() => setActiveTab('workers')}>
          👷 İşçiler
        </button>
      </div>
      {activeTab === 'contacts' ? <ContactsTab t={t} /> : <WorkersTab t={t} />}
    </div>
  );
}

function ContactsTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTACT);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('type', typeFilter);
    if (searchText) params.set('search', searchText);
    api.get(`/master/contacts?${params}`).then(r => setItems(r.data.data || [])).catch(console.error).finally(() => setLoading(false));
  }, [typeFilter, searchText]);

  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/master/contacts', form);
      toast.success(t('master.contactSaved'));
      setShowModal(false); setForm(EMPTY_CONTACT); load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/master/contacts/${id}`); toast.success(t('settings.deleted')); load(); }
    catch (err) { toast.error(err.response?.data?.error || t('settings.deleteFailed')); }
  };

  return (
    <div>
      <div className="form-group" style={{ marginBottom: 8 }}>
        <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder={t('common.search')} />
      </div>
      <div className="chip-group">
        {CONTACT_TYPES.map(ct => (
          <button key={ct.value} className={`chip${typeFilter === ct.value ? ' active' : ''}`} onClick={() => setTypeFilter(ct.value)}>{ct.label}</button>
        ))}
      </div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">🏪</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">{item.company_name || item.name}</div>
                <div className="txn-meta">
                  <span style={{ background: item.type === 'buyer' ? '#e8f5e4' : item.type === 'seller' ? '#fef3e4' : '#e4ecf5',
                    padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700, marginRight: 4 }}>
                    {item.type === 'buyer' ? 'Alıcı' : item.type === 'seller' ? 'Satıcı' : 'Her İkisi'}
                  </span>
                  {item.contact_category && <span style={{ fontSize: '0.72rem', color: '#888' }}>
                    {CONTACT_CATEGORIES.find(c => c.value === item.contact_category)?.label || item.contact_category}
                  </span>}
                  {item.city && ` · ${item.city}`}
                  {item.phone && ` · 📞 ${item.phone}`}
                </div>
                {item.created_by_farm_name && <div style={{ fontSize: '0.68rem', color: '#bbb', marginTop: 2 }}>{t('master.addedBy')}: {item.created_by_farm_name}</div>}
              </div>
              <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
      </div>
      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>
      {showModal && (
        <Modal title={t('master.addContact')} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {[{v:'buyer',l:'🛒 Alıcı'},{v:'seller',l:'🏪 Satıcı'},{v:'both',l:'↔️ Her İkisi'}].map(o => (
                  <button key={o.v} type="button" onClick={() => set('type', o.v)}
                    style={{ flex: 1, padding: '8px', borderRadius: 8, border: form.type === o.v ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: form.type === o.v ? 'var(--green-pale)' : '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Ad / Firma *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="İsim veya firma adı..." required />
            </div>
            <div className="form-row">
              <div className="form-group"><label>Kategori</label>
                <select value={form.contactCategory} onChange={e => set('contactCategory', e.target.value)}>
                  {CONTACT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select></div>
              <div className="form-group"><label>Telefon</label>
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="05XX..." /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>İl</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
              <div className="form-group"><label>İlçe</label><input value={form.district} onChange={e => set('district', e.target.value)} /></div>
            </div>
            <div className="form-group"><label>Notlar</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>{t('common.save')}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function WorkersTab({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_WORKER);
  const [tcError, setTcError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = searchText ? `?search=${encodeURIComponent(searchText)}` : '';
    api.get(`/workers${params}`).then(r => setItems(r.data.data || [])).catch(console.error).finally(() => setLoading(false));
  }, [searchText]);

  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleWorkType = (wt) => {
    setForm(f => ({
      ...f,
      workTypes: f.workTypes.includes(wt) ? f.workTypes.filter(w => w !== wt) : [...f.workTypes, wt]
    }));
  };

  const handleTcChange = (val) => {
    set('identityNumber', val);
    if (form.identityType === 'tc' && val.length === 11) {
      const result = validateTCKimlik(val);
      setTcError(result.valid ? '' : result.error);
    } else { setTcError(''); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.identityType === 'tc' && form.identityNumber) {
      const result = validateTCKimlik(form.identityNumber);
      if (!result.valid) { setTcError(result.error); return; }
    }
    try {
      await api.post('/workers', form);
      toast.success(t('settings.saved'));
      setShowModal(false); setForm(EMPTY_WORKER); setTcError(''); load();
    } catch (err) { toast.error(err.response?.data?.error || t('settings.saveFailed')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/workers/${id}`); toast.success(t('settings.deleted')); load(); }
    catch { toast.error(t('settings.deleteFailed')); }
  };

  return (
    <div>
      <div className="form-group" style={{ marginBottom: 8 }}>
        <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="İşçi ara..." />
      </div>
      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? <div className="empty-state"><div className="icon">👷</div><p>{t('common.noData')}</p></div>
          : items.map(item => (
            <div key={item.id} className="txn-item">
              <div className="txn-main">
                <div className="txn-name">
                  {{ tc: '🇹🇷', yabanci: '🌍', gecici: '👷' }[item.identity_type] || ''} {item.first_name} {item.last_name}
                </div>
                <div className="txn-meta">
                  {item.default_daily_rate && `₺${item.default_daily_rate}/${item.preferred_unit || 'gün'}`}
                  {item.phone && ` · ${item.phone}`}
                  {item.is_own === false && <span style={{ background: '#e4ecf5', padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700, marginLeft: 4 }}>Bölgesel</span>}
                </div>
                {item.work_types && item.work_types.length > 0 && (
                  <div style={{ marginTop: 2 }}>
                    {item.work_types.map(wt => (
                      <span key={wt} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, background: '#f0f0f0', fontSize: '0.68rem', marginRight: 3 }}>{wt}</span>
                    ))}
                  </div>
                )}
              </div>
              {item.is_own !== false && (
                <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
              )}
            </div>
          ))}
      </div>
      <button className="btn-fab" onClick={() => setShowModal(true)}>+</button>
      {showModal && (
        <Modal title="Yeni İşçi Ekle" onClose={() => { setShowModal(false); setTcError(''); }}>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group"><label>Ad *</label>
                <input value={form.firstName} onChange={e => set('firstName', e.target.value)} required /></div>
              <div className="form-group"><label>Soyad *</label>
                <input value={form.lastName} onChange={e => set('lastName', e.target.value)} required /></div>
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', gap: 4 }}>
                {[{v:'tc',l:'🇹🇷 TC'},{v:'yabanci',l:'🌍 Yabancı'},{v:'gecici',l:'👷 Geçici'}].map(o => (
                  <button key={o.v} type="button" onClick={() => set('identityType', o.v)}
                    style={{ flex: 1, padding: '6px', borderRadius: 6, border: form.identityType === o.v ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: form.identityType === o.v ? 'var(--green-pale)' : '#fff', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>{form.identityType === 'tc' ? 'TC Kimlik No' : form.identityType === 'yabanci' ? 'Pasaport / İzin No' : 'Belge No'}</label>
              <input value={form.identityNumber} onChange={e => handleTcChange(e.target.value)}
                placeholder={form.identityType === 'tc' ? '11 haneli TC Kimlik No' : 'Belge numarası'} />
              {tcError && <div style={{ color: '#e74c3c', fontSize: '0.75rem', marginTop: 2 }}>{tcError}</div>}
            </div>
            <div className="form-row">
              <div className="form-group"><label>Telefon</label><input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group"><label>Günlük Ücret (₺)</label><input type="number" value={form.defaultDailyRate} onChange={e => set('defaultDailyRate', e.target.value)} /></div>
            </div>
            <div className="form-group">
              <label>İş Türleri</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {WORK_TYPES_LIST.map(wt => (
                  <button key={wt} type="button" onClick={() => toggleWorkType(wt)}
                    style={{ padding: '4px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                      border: form.workTypes.includes(wt) ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: form.workTypes.includes(wt) ? 'var(--green-pale)' : '#fff' }}>
                    {wt}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isRegional} onChange={e => set('isRegional', e.target.checked)} />
                Bölgesel işçi (diğer çiftçiler görebilsin)
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>{t('common.save')}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
